// src/components/Client/ClientList.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  Pagination,
  PaginationItem,
  PaginationLink,
  Input,
  Col,
  Row,
  Label,
  Spinner,
  Badge,
} from "reactstrap";
import NewClientModal from "./NewClientModal";
import ConfirmRemovalModal from "./ConfirmRemovalModal";
import Moment from "moment";
import axios from "axios";
import { API_URL, API_URL_sedes } from "../../constants";

import { getAuthHeaders } from "../../utils/auth";
const COL_STYLES = {
  status: { maxWidth: "8rem", minWidth: "8rem" },
  razon: { maxWidth: "16rem", minWidth: "10rem" },
  idFisc: { maxWidth: "10rem", minWidth: "8rem" },
  nombre: { maxWidth: "12rem", minWidth: "10rem" },
  giro: { maxWidth: "10rem", minWidth: "8rem" },
  sede: { maxWidth: "11rem", minWidth: "9rem" },
  comp: { maxWidth: "12rem", minWidth: "10rem" },
  cxp: { maxWidth: "12rem", minWidth: "10rem" },
  email: { maxWidth: "16rem", minWidth: "12rem" },
  phone: { maxWidth: "11rem", minWidth: "9rem" },
  pais: { maxWidth: "9rem", minWidth: "7rem" },
  cp: { maxWidth: "8rem", minWidth: "6rem" },
  estado: { maxWidth: "11rem", minWidth: "9rem" },
  ciudad: { maxWidth: "11rem", minWidth: "9rem" },
  calle: { maxWidth: "22rem", minWidth: "15rem" },
  fecha: { maxWidth: "12rem", minWidth: "10rem" },
  actions: { maxWidth: "10rem", minWidth: "8rem" },
};

// Custom hook for debouncing
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// Custom hook for managing expanded rows
const useExpandedRows = () => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = useCallback((pk) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(pk)) {
        next.delete(pk);
      } else {
        next.add(pk);
      }
      return next;
    });
  }, []);

  const isExpanded = useCallback(
    (pk) => expandedRows.has(pk),
    [expandedRows]
  );

  return { expandedRows, toggleRow, isExpanded };
};

const ClientList = ({
  clients = [],
  itemsPerPage = 20,
  resetState,
  onFilteredChange,
}) => {
  // State
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [sedeFilter, setSedeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("Activo");
  const [sedeLookup, setSedeLookup] = useState({});
  const [isLoadingSedes, setIsLoadingSedes] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(null);

  const { expandedRows, toggleRow, isExpanded } = useExpandedRows();
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Fetch sedes on mount
  useEffect(() => {
    fetchSedes();
  }, []);

  const fetchSedes = async () => {
    setIsLoadingSedes(true);

    try {
      const headers = getAuthHeaders();
      const res = await axios.get(API_URL_sedes, {
        headers: {
          ...headers,
          Accept: "application/json",
        },
        timeout: 10000,
      });

      const lookup = {};
      if (Array.isArray(res.data)) {
        res.data.forEach((s) => {
          if (s?.id != null) {
            lookup[s.id] = s.sede || s.name || s.label || "";
          }
        });
      }

      setSedeLookup(lookup);
    } catch (err) {
      console.error("Error fetching sedes:", err);
    } finally {
      setIsLoadingSedes(false);
    }
  };

  // Helper functions
  const lc = useCallback((v) => (v || "").toString().toLowerCase(), []);

  const getSedeLabel = useCallback(
    (client) => {
      const raw = client?.sede;

      if (raw && typeof raw === "object") {
        return raw.sede || raw.name || raw.label || "";
      }

      if (sedeLookup[raw]) {
        return sedeLookup[raw];
      }

      return raw != null ? String(raw) : "";
    },
    [sedeLookup]
  );

  const getClientPk = useCallback((client) => client.pk ?? client.id, []);

  // Filtered data with memoization
  const filteredData = useMemo(() => {
    return clients.filter((client) => {
      const clientStatus = lc(client.status || "Activo");
      const sedeLabel = getSedeLabel(client);

      // Status filter
      const matchesStatus =
        !statusFilter || clientStatus === lc(statusFilter);

      // Sede filter
      const matchesSede =
        !sedeFilter || lc(sedeLabel).includes(lc(sedeFilter));

      // Search filter
      const matchesSearch =
        !debouncedSearchQuery ||
        lc(client.razon_social).includes(lc(debouncedSearchQuery)) ||
        lc(client.id_fiscal).includes(lc(debouncedSearchQuery)) ||
        lc(client.nombre).includes(lc(debouncedSearchQuery)) ||
        lc(client.giro).includes(lc(debouncedSearchQuery)) ||
        lc(client.nombre_comprador).includes(lc(debouncedSearchQuery)) ||
        lc(client.nombre_admin).includes(lc(debouncedSearchQuery)) ||
        lc(client.email).includes(lc(debouncedSearchQuery)) ||
        lc(client.phone).includes(lc(debouncedSearchQuery)) ||
        lc(client.pais).includes(lc(debouncedSearchQuery)) ||
        lc(client.codigo_postal).includes(lc(debouncedSearchQuery)) ||
        lc(client.estado).includes(lc(debouncedSearchQuery)) ||
        lc(client.ciudad).includes(lc(debouncedSearchQuery)) ||
        lc(client.calle).includes(lc(debouncedSearchQuery)) ||
        lc(client.client_code).includes(lc(debouncedSearchQuery)) ||
        lc(sedeLabel).includes(lc(debouncedSearchQuery));

      return matchesStatus && matchesSede && matchesSearch;
    });
  }, [
    clients,
    statusFilter,
    sedeFilter,
    debouncedSearchQuery,
    lc,
    getSedeLabel,
  ]);

  // Notify parent of filtered changes
  useEffect(() => {
    if (typeof onFilteredChange === "function") {
      onFilteredChange(filteredData);
    }
  }, [filteredData, onFilteredChange]);

  // Pagination calculations
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  }, [filteredData.length, itemsPerPage]);

  const safePage = useMemo(() => {
    return Math.min(Math.max(currentPage, 1), totalPages);
  }, [currentPage, totalPages]);

  const currentData = useMemo(() => {
    const indexOfLastItem = safePage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredData.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredData, safePage, itemsPerPage]);

  // Unique sedes for filter dropdown
  const uniqueSedes = useMemo(() => {
    const sedesSet = new Set(
      clients.map((c) => getSedeLabel(c)).filter(Boolean)
    );
    return Array.from(sedesSet).sort();
  }, [clients, getSedeLabel]);

  // Event handlers
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleSedeFilterChange = useCallback((e) => {
    setSedeFilter(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleStatusFilterChange = useCallback((e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleToggleKeyDown = useCallback(
    (pk) => (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggleRow(pk);
      }
    },
    [toggleRow]
  );

  const handleStatusToggle = async (client) => {
    const clientPk = getClientPk(client);
    if (!clientPk) {
      console.error("Client missing pk/id:", client);
      return;
    }

    const currentStatus = (client.status || "Activo").toString();
    const nextStatus = currentStatus === "Activo" ? "Inactivo" : "Activo";

    setIsTogglingStatus(clientPk);

    try {
      const base = String(API_URL);
      const url = base.endsWith("/")
        ? `${base}${clientPk}/`
        : `${base}/${clientPk}/`;

      const headers = getAuthHeaders();
      await axios.patch(
        url,
        { status: nextStatus },
        {
          headers: {
            ...headers,
            Accept: "application/json",
          },
          timeout: 10000,
        }
      );

      // Refresh data after successful toggle
      if (typeof resetState === "function") {
        resetState();
      }
    } catch (err) {
      console.error("Error toggling status:", err);
      // Could add toast notification here
    } finally {
      setIsTogglingStatus(null);
    }
  };

  // Render cell with expand/collapse
  const renderCell = useCallback(
    (pk, text, style) => {
      const expanded = isExpanded(pk);
      const displayText = text || "";

      return (
        <td style={style} title={displayText}>
          <div
            className={`text-break ${expanded ? "" : "text-truncate"} cell-toggle`}
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            aria-label={
              expanded ? "Click to collapse" : "Click to expand full text"
            }
            onClick={() => toggleRow(pk)}
            onKeyDown={handleToggleKeyDown(pk)}
            style={{ cursor: "pointer" }}
          >
            {displayText}
          </div>
        </td>
      );
    },
    [isExpanded, toggleRow, handleToggleKeyDown]
  );

  // Status badge component
  const StatusBadge = useCallback(
    ({ client }) => {
      const clientPk = getClientPk(client);
      const currentStatus = client.status || "Activo";
      const isToggling = isTogglingStatus === clientPk;

      return (
        <div className="d-flex align-items-center justify-content-center">
          <div className="form-check form-switch">
            <Input
              type="switch"
              role="switch"
              className="form-check-input"
              checked={currentStatus === "Activo"}
              onChange={() => handleStatusToggle(client)}
              disabled={isToggling}
              aria-label={`Toggle status for ${client.razon_social}`}
            />
          </div>
          <Badge
            color={
              currentStatus === "Activo"
                ? "success"
                : currentStatus === "Inactivo"
                ? "secondary"
                : "warning"
            }
            className="ms-2"
          >
            {isToggling ? "..." : currentStatus}
          </Badge>
        </div>
      );
    },
    [isTogglingStatus, getClientPk, handleStatusToggle]
  );

  // Pagination component
  const PaginationControls = useMemo(() => {
    if (totalPages <= 1) return null;

    // Show max 7 page buttons for better UX
    const maxButtons = 7;
    let startPage = Math.max(1, safePage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    const pageButtons = [];
    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <PaginationItem key={i} active={safePage === i}>
          <PaginationLink
            onClick={() => handlePageChange(i)}
            aria-label={`Go to page ${i}`}
            aria-current={safePage === i ? "page" : undefined}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return (
      <Pagination size="lg" aria-label="Client list pagination">
        <PaginationItem disabled={safePage === 1}>
          <PaginationLink
            previous
            onClick={() => handlePageChange(safePage - 1)}
            aria-label="Go to previous page"
          />
        </PaginationItem>

        {startPage > 1 && (
          <>
            <PaginationItem>
              <PaginationLink onClick={() => handlePageChange(1)}>
                1
              </PaginationLink>
            </PaginationItem>
            {startPage > 2 && (
              <PaginationItem disabled>
                <PaginationLink>...</PaginationLink>
              </PaginationItem>
            )}
          </>
        )}

        {pageButtons}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && (
              <PaginationItem disabled>
                <PaginationLink>...</PaginationLink>
              </PaginationItem>
            )}
            <PaginationItem>
              <PaginationLink onClick={() => handlePageChange(totalPages)}>
                {totalPages}
              </PaginationLink>
            </PaginationItem>
          </>
        )}

        <PaginationItem disabled={safePage === totalPages}>
          <PaginationLink
            next
            onClick={() => handlePageChange(safePage + 1)}
            aria-label="Go to next page"
          />
        </PaginationItem>
      </Pagination>
    );
  }, [totalPages, safePage, handlePageChange]);

  // Main render
  return (
    <div>
      {/* Filters */}
      <Row className="mb-3">
        <Col md={4}>
          <Label htmlFor="sede-filter">Value Stream</Label>
          <Input
            type="select"
            id="sede-filter"
            value={sedeFilter}
            onChange={handleSedeFilterChange}
            disabled={isLoadingSedes}
            aria-label="Filter by Value Stream"
          >
            <option value="">Todos los Value Streams</option>
            {uniqueSedes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Input>
        </Col>

        <Col md={4}>
          <Label htmlFor="status-filter">Status</Label>
          <Input
            type="select"
            id="status-filter"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            aria-label="Filter by status"
          >
            <option value="">Todos</option>
            <option value="Activo">Activo</option>
            <option value="Inactivo">Inactivo</option>
            <option value="Prospecto">Prospecto</option>
          </Input>
        </Col>

        <Col md={4}>
          <Label htmlFor="search-input">Buscar</Label>
          <Input
            type="text"
            id="search-input"
            placeholder="Buscar clientes..."
            value={searchQuery}
            onChange={handleSearchChange}
            aria-label="Search clients"
          />
          {debouncedSearchQuery !== searchQuery && (
            <small className="text-muted">Buscando...</small>
          )}
        </Col>
      </Row>

      {/* Results summary */}
      <div className="mb-2">
        <small className="text-muted">
          Mostrando {currentData.length} de {filteredData.length} cliente(s)
          {filteredData.length !== clients.length &&
            ` (filtrado de ${clients.length} total)`}
        </small>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <Table hover striped className="align-middle">
          <thead className="table-light">
            <tr>
              <th style={COL_STYLES.status}>Status</th>
              <th style={COL_STYLES.razon}>Razón Social</th>
              <th style={COL_STYLES.idFisc}>ID Fiscal</th>
              <th style={COL_STYLES.nombre}>Nombre Comercial</th>
              <th style={COL_STYLES.giro}>Giro</th>
              <th style={COL_STYLES.sede}>Value Stream</th>
              <th style={COL_STYLES.comp}>Comprador</th>
              <th style={COL_STYLES.cxp}>C x P</th>
              <th style={COL_STYLES.email}>Correo</th>
              <th style={COL_STYLES.phone}>Teléfono</th>
              <th style={COL_STYLES.pais}>País</th>
              <th style={COL_STYLES.cp}>CP</th>
              <th style={COL_STYLES.estado}>Estado</th>
              <th style={COL_STYLES.ciudad}>Ciudad</th>
              <th style={COL_STYLES.calle}>Dirección</th>
              <th style={COL_STYLES.fecha}>Fecha Registro</th>
              <th style={COL_STYLES.actions}>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {currentData.length === 0 ? (
              <tr>
                <td colSpan="17" className="text-center py-5">
                  {clients.length === 0 ? (
                    <div>
                      <h5>No hay clientes registrados</h5>
                      <p className="text-muted">
                        Comienza agregando tu primer cliente
                      </p>
                    </div>
                  ) : (
                    <div>
                      <h5>No se encontraron resultados</h5>
                      <p className="text-muted">
                        Intenta ajustar los filtros de búsqueda
                      </p>
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              currentData.map((client) => {
                const clientPk = getClientPk(client);
                const sedeLabel = getSedeLabel(client);

                return (
                  <tr key={clientPk}>
                    <td style={COL_STYLES.status}>
                      <StatusBadge client={client} />
                    </td>

                    {renderCell(clientPk, client.razon_social, COL_STYLES.razon)}
                    {renderCell(clientPk, client.id_fiscal, COL_STYLES.idFisc)}
                    {renderCell(clientPk, client.nombre, COL_STYLES.nombre)}
                    {renderCell(clientPk, client.giro, COL_STYLES.giro)}
                    {renderCell(clientPk, sedeLabel, COL_STYLES.sede)}
                    {renderCell(
                      clientPk,
                      client.nombre_comprador,
                      COL_STYLES.comp
                    )}
                    {renderCell(clientPk, client.nombre_admin, COL_STYLES.cxp)}
                    {renderCell(clientPk, client.email, COL_STYLES.email)}
                    {renderCell(clientPk, client.phone, COL_STYLES.phone)}
                    {renderCell(clientPk, client.pais, COL_STYLES.pais)}
                    {renderCell(
                      clientPk,
                      client.codigo_postal,
                      COL_STYLES.cp
                    )}
                    {renderCell(clientPk, client.estado, COL_STYLES.estado)}
                    {renderCell(clientPk, client.ciudad, COL_STYLES.ciudad)}
                    {renderCell(clientPk, client.calle, COL_STYLES.calle)}

                    <td style={COL_STYLES.fecha}>
                      <div className="text-truncate">
                        {client.registration_date
                          ? Moment(client.registration_date).format(
                              "MMM DD, YYYY HH:mm"
                            )
                          : "—"}
                      </div>
                    </td>

                    <td style={COL_STYLES.actions} className="text-nowrap">
                      <div className="d-flex gap-1">
                        <NewClientModal
                          create={false}
                          client={client}
                          resetState={resetState}
                        />
                        <ConfirmRemovalModal
                          pk={clientPk}
                          resetState={resetState}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {PaginationControls}
    </div>
  );
};

export default ClientList;